import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useConnectionStatusStore } from '../stores/connectionStatusStore';

export type EventSourceStatus = 'connecting' | 'open' | 'closed' | 'error';

export interface UseEventSourceOptions<T> {
  enabled?: boolean;
  events?: Record<string, (data: T) => void>;
  /** Optionaler Name für den connectionStatusStore. Wenn gesetzt, wird der
   *  Verbindungsstatus im Store registriert und aktuell gehalten. */
  name?: string;
}

export interface UseEventSourceResult<T> {
  data: T | null;
  status: EventSourceStatus;
  error: Error | null;
  reconnect: () => void;
}

const BACKOFF_STEPS = [1000, 2000, 4000, 8000, 30000];

export function useEventSource<T>(
  url: string,
  opts?: UseEventSourceOptions<T>
): UseEventSourceResult<T> {
  const { enabled = true, events = {}, name } = opts || {};

  const [data, setData] = useState<T | null>(null);
  const [status, setStatus] = useState<EventSourceStatus>('closed');
  const [error, setError] = useState<Error | null>(null);

  const registerConnection = useConnectionStatusStore((s) => s.registerConnection);
  const updateStatus = useConnectionStatusStore((s) => s.updateStatus);
  const incrementReconnect = useConnectionStatusStore((s) => s.incrementReconnect);
  const unregisterConnection = useConnectionStatusStore((s) => s.unregisterConnection);

  // Stabiler Ref auf den aktuellen Namen, damit Store-Aufrufe keine Extra-Deps brauchen
  const nameRef = useRef(name);
  useEffect(() => {
    nameRef.current = name;
  }, [name]);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const eventsRef = useRef(events);

  const accessToken = useAuthStore((state) => state.accessToken);

  // Keep events ref up to date to avoid stale closures
  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  const clearTimeouts = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  };

  const closeConnection = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    clearTimeouts();
    setStatus('closed');
  }, []);

  const connectRef = useRef<() => void>(() => {});

  const connect = useCallback(() => {
    if (!enabled) {
      closeConnection();
      return;
    }

    closeConnection();

    setStatus('connecting');
    setError(null);

    try {
      // Build URL with access token
      const baseUrl = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env.VITE_API_BASE_URL : window.location?.origin || 'http://localhost';
      const fullUrl = new URL(url, baseUrl || window.location?.origin || 'http://localhost');
      if (accessToken) {
        fullUrl.searchParams.set('access_token', accessToken);
      }

      const es = new EventSource(fullUrl.toString());
      eventSourceRef.current = es;

      const resetHeartbeat = () => {
        if (heartbeatTimeoutRef.current) {
          clearTimeout(heartbeatTimeoutRef.current);
        }
        heartbeatTimeoutRef.current = setTimeout(() => {
          // Heartbeat timeout reached, reconnect
          closeConnection();
          if (connectRef.current) {
            connectRef.current();
          }
        }, 60000); // 60 seconds
      };

      es.onopen = () => {
        setStatus('open');
        reconnectAttemptsRef.current = 0;
        resetHeartbeat();
      };

      es.onmessage = (event) => {
        resetHeartbeat();
        try {
          const parsedData = JSON.parse(event.data) as T;
          setData(parsedData);
        } catch {
          setStatus('error');
          setError(new Error('Failed to parse event data'));
        }
      };

      es.onerror = () => {
        setStatus('error');
        setError(new Error('EventSource connection error'));
        es.close();

        // Schedule reconnect
        const attempt = Math.min(reconnectAttemptsRef.current, BACKOFF_STEPS.length - 1);
        const delay = BACKOFF_STEPS[attempt];

        reconnectAttemptsRef.current += 1;

        clearTimeouts();
        reconnectTimeoutRef.current = setTimeout(() => {
          if (connectRef.current) {
            connectRef.current();
          }
        }, delay);
      };

      // Register named events
      Object.keys(eventsRef.current).forEach((eventName) => {
        es.addEventListener(eventName, (event: MessageEvent) => {
          resetHeartbeat();
          try {
            const parsedData = JSON.parse(event.data) as T;
            eventsRef.current[eventName](parsedData);
          } catch (err) {
            setStatus('error');
            setError(err instanceof Error ? err : new Error('Failed to parse named event data'));
          }
        });
      });

      // Setup heartbeat listener explicitly if there's a heartbeat event, otherwise any event resets it
      es.addEventListener('heartbeat', () => {
         resetHeartbeat();
      });

    } catch {
      setStatus('error');
      setError(new Error('Failed to create EventSource'));
    }
  }, [enabled, url, accessToken, closeConnection]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const reconnect = useCallback(() => {
    closeConnection();
    connect();
  }, [closeConnection, connect]);

  useEffect(() => {
    if (enabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      connect();
    }

    return () => {
      closeConnection();
    };
  }, [connect, closeConnection, enabled]);

  // Verbindung im Store registrieren/abmelden (nur wenn name gesetzt)
  useEffect(() => {
    if (!name) return;
    registerConnection(name);
    return () => {
      unregisterConnection(name);
    };
    // name ändert sich in der Praxis nicht — bewusst kein name in Deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerConnection, unregisterConnection]);

  // Status-Änderungen in den Store schreiben
  useEffect(() => {
    const n = nameRef.current;
    if (!n) return;
    if (status === 'error') {
      incrementReconnect(n);
    }
    updateStatus(n, status);
  }, [status, updateStatus, incrementReconnect]);

  return { data, status, error, reconnect };
}
